import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useDarkMode } from '@/hooks'
import { AppHeader } from './AppHeader'
import { ToolLauncher } from './ToolLauncher'

/**
 * Main Layout Component (App Shell)
 *
 * This is the primary layout wrapper for all authenticated pages.
 * It provides:
 * - Persistent global header with Home and app search
 * - Transient app launcher
 * - Main content area with scroll
 *
 * Usage:
 * Wrap your routes with this layout in App.jsx
 *
 * The tool registry remains shared by Home, the launcher, and route guards.
 */
export function MainLayout({ user, onLogout }) {
  const [launcherOpen, setLauncherOpen] = useState(false)
  const [dark, setDark] = useDarkMode()

  useEffect(() => {
    const handleGlobalShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setLauncherOpen(true)
      }
    }

    window.addEventListener('keydown', handleGlobalShortcut)
    return () => window.removeEventListener('keydown', handleGlobalShortcut)
  }, [])

  return (
    <div className="flex h-dvh flex-col bg-background">
      <AppHeader
        user={user}
        onLogout={onLogout}
        dark={dark}
        onToggleDark={() => setDark((d) => !d)}
        onOpenLauncher={() => setLauncherOpen(true)}
      />
      <ToolLauncher
        open={launcherOpen}
        onOpenChange={setLauncherOpen}
        user={user}
      />

      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <Outlet context={{ user }} />
        </div>
      </main>
    </div>
  )
}

export default MainLayout
